import React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { areaNormalFontFamily } from './constants';

export interface SelectOption {
    value: string;
    label: string;
}

export interface SelectProps extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'size'
> {
    label?: string;
    options: SelectOption[];
    error?: boolean;
    helperText?: string;
}

// Accepts MUI's className so MuiSelect-iconOpen rotation works automatically
function ChevronIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <svg
            className={className}
            style={style}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
        >
            <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    function Select(
        {
            label,
            options,
            error = false,
            helperText,
            className,
            style,
            value,
            defaultValue,
            onChange,
            onFocus,
            onBlur,
            disabled,
            id,
            ...rest
        },
        ref,
    ) {
        return (
            <TextField
                id={id}
                label={label}
                error={error}
                helperText={helperText}
                disabled={disabled}
                variant="outlined"
                fullWidth
                select
                value={value}
                defaultValue={defaultValue}
                // MUI Select fires SelectChangeEvent; target.value is always a string — safe cast
                onChange={onChange ? (event) => onChange(event as unknown as React.ChangeEvent<HTMLSelectElement>) : undefined}
                className={className}
                style={style}
                slotProps={{
                    select: {
                        ...(rest as object),
                        // Forward ref to MUI's hidden native <select> element
                        inputRef: ref,
                        displayEmpty: true,
                        IconComponent: ChevronIcon,
                        onFocus: onFocus as React.FocusEventHandler<HTMLElement>,
                        onBlur: onBlur as React.FocusEventHandler<HTMLElement>,
                        MenuProps: {
                            container: document.getElementById('root'),
                            slotProps: {
                                paper: {
                                    sx: {
                                        mt: '4px',
                                        borderRadius: '8px',
                                        border: '1px solid #D0CFD9',
                                        boxShadow: '0 4px 20px rgba(10, 0, 77, 0.08)',
                                        '& .MuiMenuItem-root': {
                                            fontFamily: areaNormalFontFamily,
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: '#282636',
                                            minHeight: 48,
                                            '&.Mui-selected': {
                                                backgroundColor: '#F4F2FF',
                                                '&:hover': { backgroundColor: '#ECE8FF' },
                                            },
                                            '&:hover': { backgroundColor: '#F4F2FF' },
                                            '&:focus': { backgroundColor: '#F4F2FF' },
                                        },
                                    },
                                },
                            },
                        },
                    } as object,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: 56,
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        '& .MuiSelect-select': {
                            fontFamily: areaNormalFontFamily,
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#282636',
                            // Left / right padding always; top/bottom differ with label
                            paddingLeft: '14px !important',
                            paddingRight: '40px !important', // room for chevron at right:14
                            ...(label
                                ? {
                                    paddingTop: '27px',
                                    paddingBottom: '8px',
                                    height: '100%',
                                    boxSizing: 'border-box',
                                }
                                : {}),
                        },
                        '& .MuiSelect-icon': {
                            right: 14,
                            color: '#73717F',
                            top: 'calc(50% - 8px)',
                            transition: 'transform 0.15s ease',
                            '&.MuiSelect-iconOpen': {
                                transform: 'rotate(180deg)',
                            },
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D0CFD9',
                            borderWidth: '1px',
                            borderRadius: '8px',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        },
                        // Suppress the border notch — label floats inside the box, not on the border
                        '& .MuiOutlinedInput-notchedOutline legend': {
                            maxWidth: '0px !important',
                            padding: 0,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D0CFD9',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#715AFF',
                            borderWidth: '1px',
                            boxShadow: '0 0 0 1px #715AFF',
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E3291B',
                        },
                        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E3291B',
                            boxShadow: '0 0 0 1px #E3291B',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#F3F3F7',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#D0CFD9',
                            },
                        },
                        '&.Mui-disabled .MuiSelect-select': {
                            cursor: 'not-allowed',
                            WebkitTextFillColor: '#282636',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        fontFamily: areaNormalFontFamily,
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#73717F',
                        lineHeight: '24px',
                        // Vertically centre inside the 56 px container (16 px from top of FormControl)
                        transform: 'translate(14px, 16px) scale(1)',
                        transition: 'color 0.15s ease, transform 0.15s ease, font-size 0.15s ease, letter-spacing 0.15s ease',
                        pointerEvents: 'none',
                        '&.Mui-focused': { color: '#715AFF' },
                        '&.Mui-error': { color: '#E3291B' },
                        // Floated: keep label inside the box at 9 px from top
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, 9px) scale(1)',
                            fontSize: '14px',
                            lineHeight: '14px',
                            letterSpacing: '0.25px',
                        },
                        '&.Mui-disabled': { color: '#73717F' },
                    },
                    '& .MuiFormHelperText-root': {
                        fontFamily: areaNormalFontFamily,
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: '#73717F',
                        margin: '4px 0 0',
                        '&.Mui-error': { color: '#E3291B' },
                    },
                }}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    },
);

Select.displayName = 'Select';
